import Constants from 'expo-constants';
import { useEffect } from 'react';

import { useAuthContext } from '@fhs/auth/src/hooks';
import { useUserAccountsQuery } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { ISOs } from '@fhs-legacy/frontend/src/utils/form/constants';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { usePaymentMethodStore } from '..';
import {
  useApplePayAvailability,
  useGooglePayAvailability,
} from '../../components/checkout-components/payment-details/action-payment-method-selection/digital-wallet-availability';

/**
 * This hook calls early to prefetch payment data and populate Apollo cache to eliminate wait in checkout
 *
 * This is a "fire and forget" hook - it runs in the background and
 * updates the store.
 */
export function usePreloadPaymentMethods() {
  const enableCartService = useFlag('enable-simply-better-cart-service');
  const { user } = useAuthContext();
  const setUserAccountsData = usePaymentMethodStore(state => state.setUserAccountsData);
  const canUseApplePay = useApplePayAvailability();
  const canUseGooglePay = useGooglePayAvailability();

  const feAlpha3CountryCode =
    Constants.expoConfig?.extra?.eas.buildRegion === 'US' ? ISOs.USA : ISOs.CAN;

  // User's default payment method (set on payment methods page)
  const defaultAccountIdentifier =
    user?.details?.defaultAccountIdentifier ?? user?.details?.defaultFdAccountId;

  // use cache with apollo to fetch userAccounts info to access later in checkout
  const { data: userAccountsData } = useUserAccountsQuery({
    variables: { feCountryCode: feAlpha3CountryCode },
    fetchPolicy: 'cache-and-network',
    skip: false,
  });

  useEffect(() => {
    if (userAccountsData && enableCartService) {
      setUserAccountsData(userAccountsData, defaultAccountIdentifier, {
        canUseApplePay,
        canUseGooglePay,
      });
    }
  }, [
    userAccountsData,
    setUserAccountsData,
    enableCartService,
    defaultAccountIdentifier,
    canUseApplePay,
    canUseGooglePay,
  ]);
}
